import { useRef, useState } from 'react'
import { type NodeData, type EdgeData, useSelection, Canvas, Node, Edge } from 'reaflow'

const CLICK_DRAG_THRESHOLD_PX = 5

function findConnectedNodes(selectedNodeId: string, edges: EdgeData[]): Set<string> {
  const connected = new Set<string>([selectedNodeId])
  const queue = [selectedNodeId]

  // Build adjacency list for bidirectional traversal
  const adjacencyMap = new Map<string, string[]>()
  edges.forEach((edge) => {
    // Skip edges with undefined from/to
    if (!edge.from || !edge.to) return

    // Add both directions
    if (!adjacencyMap.has(edge.from)) adjacencyMap.set(edge.from, [])
    if (!adjacencyMap.has(edge.to)) adjacencyMap.set(edge.to, [])
    adjacencyMap.get(edge.from)!.push(edge.to)
    adjacencyMap.get(edge.to)!.push(edge.from)
  })

  // BFS traversal
  while (queue.length > 0) {
    const current = queue.shift()!
    const neighbors = adjacencyMap.get(current) || []

    neighbors.forEach((neighbor) => {
      if (!connected.has(neighbor)) {
        connected.add(neighbor)
        queue.push(neighbor)
      }
    })
  }

  return connected
}

interface OrgGraphProps {
  graph: { id: string; name: string; parentId?: string | null | undefined; type: string }[]
  onNodeClick: (node: {
    id: string
    name: string
    parentId?: string | null | undefined
    type: string
  }) => void
}
export function OrgGraph({ graph, onNodeClick }: OrgGraphProps) {
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const pointerDidDragRef = useRef(false)
  const canvasSize = Math.max(4000, graph.length * 200)

  // Detect composite user IDs and extract real ID
  const userIdPattern = /^(.+?)-(group|company)-(.+)$/

  // Separate GROUP/COMPANY nodes from USER nodes
  const groupAndCompanyNodes = graph
    .filter((node) => node.type !== 'USER')
    .map((node) => ({
      id: node.id,
      text: node.name,
    }))

  // Group all user nodes by real user ID to deduplicate
  const userNodesMap = new Map<
    string,
    {
      realId: string
      name: string
      parentIds: string[]
    }
  >()

  graph.forEach((node) => {
    if (node.type === 'USER') {
      const match = node.id.match(userIdPattern)
      if (match) {
        const [, realUserId] = match
        if (!userNodesMap.has(realUserId)) {
          userNodesMap.set(realUserId, {
            realId: realUserId,
            name: node.name,
            parentIds: node.parentId ? [node.parentId] : [],
          })
        } else {
          const existingUser = userNodesMap.get(realUserId)!
          if (node.parentId && !existingUser.parentIds.includes(node.parentId)) {
            existingUser.parentIds.push(node.parentId)
          }
        }
      }
    }
  })

  // Create deduplicated nodes
  const deduplicatedUserNodes = Array.from(userNodesMap.values()).map((user) => ({
    id: user.realId,
    text: user.name,
  }))

  const defaultNodes = [...groupAndCompanyNodes, ...deduplicatedUserNodes]

  // Create edges for GROUP/COMPANY nodes
  const groupAndCompanyEdges = graph
    .filter((node) => node.type !== 'USER' && node.parentId != undefined)
    .map((edge) => ({
      id: `${edge.id}-${edge.parentId}`,
      from: edge.id,
      to: edge.parentId as string,
    }))

  // Create edges for all user parent relationships
  const userEdges = Array.from(userNodesMap.values()).flatMap((user) =>
    user.parentIds.map((parentId) => ({
      id: `${user.realId}-${parentId}`,
      from: user.realId,
      to: parentId,
    })),
  )

  const defaultEdges = [...groupAndCompanyEdges, ...userEdges]
  const [nodes, setNodes] = useState<NodeData[]>(defaultNodes)
  const [edges, setEdges] = useState<EdgeData[]>(defaultEdges)
  const [connectedNodeIds, setConnectedNodeIds] = useState<Set<string>>(new Set())
  const [hoveredEdgeIds, setHoveredEdgeIds] = useState<Set<string>>(new Set())
  const { selections, onClick, onKeyDown, clearSelections } = useSelection({
    nodes,
    edges,
    onDataChange: (n, e) => {
      setNodes(n)
      setEdges(e)
    },
    onSelection: (s) => {
      const isClick = !pointerDidDragRef.current

      pointerStartRef.current = null
      pointerDidDragRef.current = false

      if (s.length === 1 && typeof s[0] === 'string' && isClick) {
        const selectedId = s[0]
        const userMatch = Array.from(userNodesMap.values()).find(
          (user) => user.realId === selectedId,
        )
        if (userMatch) {
          onNodeClick({
            id: userMatch.realId,
            name: userMatch.name,
            parentId: userMatch.parentIds[0],
            type: 'USER',
          })
        } else {
          const node = graph.find((node) => node.id === selectedId)
          if (node) {
            onNodeClick({
              id: node.id,
              name: node.name,
              parentId: node.parentId,
              type: node.type,
            })
          }
        }
      }

      if (s.length === 1 && typeof s[0] === 'string') {
        const connected = findConnectedNodes(s[0], edges)
        setConnectedNodeIds(connected)
      } else {
        setConnectedNodeIds(new Set())
      }
    },
  })

  function handlePointerDown(event: React.PointerEvent) {
    pointerStartRef.current = { x: event.clientX, y: event.clientY }
    pointerDidDragRef.current = false
  }

  function handlePointerUp(event: React.PointerEvent) {
    const pointerStart = pointerStartRef.current
    if (!pointerStart) return

    const distance = Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y)
    if (distance >= CLICK_DRAG_THRESHOLD_PX) {
      pointerDidDragRef.current = true
    }
  }

  return (
    <div
      className="cursor-grab active:cursor-grabbing"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      }}
    >
      <Canvas
        nodes={nodes}
        edges={edges}
        selections={selections}
        fit={true}
        pannable
        zoomable
        readonly={true}
        maxWidth={canvasSize}
        maxHeight={canvasSize}
        direction="LEFT"
        panType="drag"
        node={(nodeProps) => {
          const hasSelection = selections.length === 1
          const isConnected = connectedNodeIds.has(nodeProps.id)

          let nodeStyle = {
            ...nodeProps.style,
            opacity: 1,
            transition: 'opacity 0.2s ease-in-out',
          }

          if (hasSelection && !isConnected) {
            nodeStyle = {
              ...nodeStyle,
              opacity: 0.3,
              transition: 'opacity 0.2s ease-in-out',
            }
          }

          return (
            <Node
              {...nodeProps}
              style={nodeStyle}
              linkable={false}
              draggable={false}
              onClick={(event, node) => onClick?.(event, node)}
              onKeyDown={(event) => {
                onKeyDown?.(event)
              }}
              onRemove={() => {
                clearSelections()
              }}
              onEnter={(_event, node) => {
                const connectedEdges = edges.filter(
                  (edge) => edge.from === node.id || edge.to === node.id,
                )
                setHoveredEdgeIds(new Set(connectedEdges.map((e) => e.id)))
              }}
              onLeave={() => {
                setHoveredEdgeIds(new Set())
              }}
            >
              <rect
                x={0}
                y={0}
                width={nodeProps.width}
                height={nodeProps.height}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onPointerDown={(event) => {
                  event.stopPropagation()
                  pointerStartRef.current = { x: event.clientX, y: event.clientY }
                  pointerDidDragRef.current = false
                }}
                onPointerUp={(event) => {
                  const pointerStart = pointerStartRef.current
                  if (!pointerStart) return

                  const distance = Math.hypot(
                    event.clientX - pointerStart.x,
                    event.clientY - pointerStart.y,
                  )
                  if (distance >= CLICK_DRAG_THRESHOLD_PX) {
                    pointerDidDragRef.current = true
                  }
                }}
                onClick={(event) => {
                  if (pointerDidDragRef.current) return
                  onClick?.(event, nodeProps.properties)
                }}
              />
            </Node>
          )
        }}
        edge={(edgeProps) => {
          const isHovered = hoveredEdgeIds.has(edgeProps.id)
          const edgeStyle = {
            ...edgeProps.style,
            stroke: isHovered ? '#3b82f6' : undefined,
            strokeWidth: isHovered ? 3 : undefined,
            transition: 'stroke 0.2s ease-in-out, stroke-width 0.2s ease-in-out',
          }
          return <Edge {...edgeProps} style={edgeStyle} />
        }}
        onCanvasClick={() => {
          setConnectedNodeIds(new Set())
          setHoveredEdgeIds(new Set())
          clearSelections()
        }}
      />
    </div>
  )
}
